/**
 * Settings → Danger zone → Delete account.
 *
 * Self-contained section that renders:
 *  - the "Delete account" entry in SettingsPage, and
 *  - a modal that drives the deletion state machine:
 *      closed → input → submitting → { success | wrong-password
 *                                    | sole-admin-block | session-expired
 *                                    | network-error }
 *
 * From sole-admin-block the user can opt in to deleting those families along
 * with the account: that returns to `input` (the OPAQUE session is single-use,
 * so the password is asked again) with `disbandOrgs` set, and the next submit
 * sends their orgIds to the server.
 *
 * The component owns the modal state machine. The parent SettingsPage owns
 * the post-success "clear local state + navigate to sign-in" responsibility
 * via the `onAccountDeleted` callback — kept at the UI boundary so this file
 * has no direct dependency on SafeContext.logout() or the router.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HbsModal } from './hbs/Modal';
import { HbsButton } from './hbs/Button';
import { HbsInput } from './hbs/Input';
import { EyeToggle } from './hbs/EyeToggle';
import {
  runDeleteAccountHandshake,
  type SoleAdminBlocker,
} from '../auth/deleteAccountHandshake';

type ModalState =
  | { kind: 'closed' }
  | { kind: 'input'; inlineError: string | null }
  | { kind: 'submitting' }
  | { kind: 'sole-admin-block'; orgs: SoleAdminBlocker[] }
  | { kind: 'session-expired' }
  | { kind: 'network-error'; message: string }
  | { kind: 'success' };

interface DeleteAccountSectionProps {
  /** Called once the server has confirmed the account is gone. The parent
   *  should clear local vault state + navigate to /auth. */
  onAccountDeleted: () => void | Promise<void>;
}

function formatOrgList(orgs: SoleAdminBlocker[]): string {
  const names = orgs.map((o) => o.orgName);
  if (names.length === 0) return 'your family';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function DeleteAccountSection({
  onAccountDeleted,
}: DeleteAccountSectionProps): JSX.Element {
  const navigate = useNavigate();
  const [state, setState] = useState<ModalState>({ kind: 'closed' });
  // The password input lives at this level so submitting can read it without
  // losing focus, and wrong-password reset can clear it.
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  // Families the user confirmed should be deleted along with the account
  // (set from the sole-admin-block body); null = plain account deletion.
  const [disbandOrgs, setDisbandOrgs] = useState<SoleAdminBlocker[] | null>(null);

  function openModal(): void {
    setReveal(false);
    setPassword('');
    setDisbandOrgs(null);
    setState({ kind: 'input', inlineError: null });
  }

  function closeModal(): void {
    if (state.kind === 'submitting') return; // never abandon mid-flight
    setReveal(false);
    setPassword('');
    setDisbandOrgs(null);
    setState({ kind: 'closed' });
  }

  async function handleSubmit(): Promise<void> {
    if (password.length === 0) return;
    setState({ kind: 'submitting' });
    const result = disbandOrgs
      ? await runDeleteAccountHandshake(password, { disbandOrgIds: disbandOrgs.map((o) => o.orgId) })
      : await runDeleteAccountHandshake(password);
    switch (result.kind) {
      case 'success':
        setPassword('');
        setState({ kind: 'success' });
        await onAccountDeleted();
        return;
      case 'wrong-password':
        setPassword('');
        setState({
          kind: 'input',
          inlineError: "Password didn't match. Try again.",
        });
        return;
      case 'sole-admin-block':
        // Also reached after opting in, if another family became a blocker
        // since the warning — show the fresh list and ask again.
        setPassword('');
        setDisbandOrgs(null);
        setState({ kind: 'sole-admin-block', orgs: result.orgs });
        return;
      case 'session-expired':
        setPassword('');
        setState({ kind: 'session-expired' });
        return;
      case 'network-error':
        setState({ kind: 'network-error', message: result.message });
        return;
    }
  }

  function handleOpenFamilySettings(): void {
    setPassword('');
    setState({ kind: 'closed' });
    navigate('/orgs');
  }

  function handleDeleteWithFamilies(orgs: SoleAdminBlocker[]): void {
    setPassword('');
    setReveal(false);
    setDisbandOrgs(orgs);
    setState({ kind: 'input', inlineError: null });
  }

  async function handleSignInAgain(): Promise<void> {
    // The session is already invalid on the server, so we want the parent's
    // logout-and-navigate behavior (same as `onAccountDeleted`).
    setPassword('');
    setState({ kind: 'closed' });
    await onAccountDeleted();
  }

  function handleTryAgain(): void {
    setPassword('');
    setState({ kind: 'input', inlineError: null });
  }

  const open = state.kind !== 'closed';
  const inFlight = state.kind === 'submitting';

  return (
    <>
      {/* Section in SettingsPage */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <h2 className="font-display text-[18px] text-hbs-text mb-4">
          Danger zone
        </h2>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Delete account
            </p>
            <p className="text-[13px] text-hbs-text-mute">
              Permanently erase your safe and cancel any active subscription.
              This can't be undone.
            </p>
          </div>
          {/* shrink-0 pins the button at its natural inline-flex width.
              Without it, flex layout shrinks this column when the sibling
              description column is long (the Delete-account paragraph is
              longer than Subscription's, which is why this section wrapped
              but Subscription didn't). The description column carries the
              existing `min-w-0` and will wrap if space is tight. */}
          <div className="flex flex-col items-end gap-2 shrink-0">
            <HbsButton variant="subtle" id="delete-account-open-button" onClick={openModal}>
              Delete account
            </HbsButton>
          </div>
        </div>
      </section>

      {/* Modal — only rendered when open */}
      {open && (
        <HbsModal
          open={open}
          onClose={closeModal}
          title={titleFor(state, disbandOrgs)}
          size="md"
          dismissOnBackdrop={!inFlight}
          dismissOnEsc={!inFlight}
        >
          {(state.kind === 'input' || state.kind === 'submitting') && (
            <InputBody
              password={password}
              onPasswordChange={setPassword}
              reveal={reveal}
              onRevealChange={setReveal}
              inlineError={state.kind === 'input' ? state.inlineError : null}
              busy={state.kind === 'submitting'}
              disbandOrgs={disbandOrgs}
              onSubmit={() => { void handleSubmit(); }}
              onCancel={closeModal}
            />
          )}
          {state.kind === 'sole-admin-block' && (
            <SoleAdminBlockBody
              orgs={state.orgs}
              onCancel={closeModal}
              onOpenFamilySettings={handleOpenFamilySettings}
              onDeleteWithFamilies={() => handleDeleteWithFamilies(state.orgs)}
            />
          )}
          {state.kind === 'session-expired' && (
            <SessionExpiredBody
              onCancel={closeModal}
              onSignIn={() => { void handleSignInAgain(); }}
            />
          )}
          {state.kind === 'network-error' && (
            <NetworkErrorBody
              message={state.message}
              onCancel={closeModal}
              onTryAgain={handleTryAgain}
            />
          )}
          {state.kind === 'success' && (
            <p className="text-hbs-text-mute">Account deleted. Signing you out…</p>
          )}
        </HbsModal>
      )}
    </>
  );
}

function familyNoun(orgs: SoleAdminBlocker[]): string {
  return orgs.length > 1 ? 'families' : 'family';
}

function titleFor(state: ModalState, disbandOrgs: SoleAdminBlocker[] | null): string {
  switch (state.kind) {
    case 'input':
    case 'submitting':
      return disbandOrgs ? `Delete account and ${familyNoun(disbandOrgs)}` : 'Delete account';
    case 'sole-admin-block':
      return "Can't delete your account";
    case 'session-expired':
      return 'Your session timed out';
    case 'network-error':
      return "Couldn't complete deletion";
    case 'success':
      return 'Account deleted';
    default:
      return 'Delete account';
  }
}

interface InputBodyProps {
  password: string;
  onPasswordChange: (next: string) => void;
  reveal: boolean;
  onRevealChange: (next: boolean) => void;
  inlineError: string | null;
  busy: boolean;
  /** Families that will be deleted with the account, or null for none. */
  disbandOrgs: SoleAdminBlocker[] | null;
  onSubmit: () => void;
  onCancel: () => void;
}

function InputBody({
  password,
  onPasswordChange,
  reveal,
  onRevealChange,
  inlineError,
  busy,
  disbandOrgs,
  onSubmit,
  onCancel,
}: InputBodyProps): JSX.Element {
  const canSubmit = password.length > 0 && !busy;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit();
      }}
      className="space-y-3"
    >
      <p className="text-hbs-text-mute text-[13px]">
        This permanently removes your safe and cancels any active subscription.
        You can't recover your data after this.
      </p>
      <ul className="text-hbs-text-mute text-[13px] list-disc pl-5 space-y-1">
        <li>Items in your safe will be erased.</li>
        <li>Your subscription will be cancelled.</li>
        <li>
          Your recovery contacts and the people who chose you as their contact
          will be notified.
        </li>
      </ul>
      {disbandOrgs && (
        <div className="rounded-hbs-xl border border-hbs-danger/40 bg-hbs-danger/10 p-3 text-[13px] text-hbs-text space-y-2">
          <p className="font-semibold">
            {disbandOrgs.length > 1 ? 'These families' : 'This family'} will also be deleted:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            {disbandOrgs.map((o) => (
              <li key={o.orgId}>
                {o.orgName} ({o.memberCount} {o.memberCount === 1 ? 'member' : 'members'})
              </li>
            ))}
          </ul>
          <p>
            Everyone in {disbandOrgs.length > 1 ? 'them' : 'it'} loses access to
            everything shared there, and it can't be restored. Members won't be
            notified.
          </p>
        </div>
      )}
      <HbsInput
        type={reveal ? 'text' : 'password'}
        label="Enter your safe password to confirm"
        value={password}
        onChange={onPasswordChange}
        mono={reveal}
        autoFocus
        readOnly={busy}
        trailingActions={<EyeToggle revealed={reveal} onChange={onRevealChange} />}
      />
      {inlineError && (
        <p role="alert" className="text-hbs-danger text-[13px]">
          {inlineError}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <HbsButton type="button" variant="ghost" id="delete-account-cancel-button" onClick={onCancel} disabled={busy}>
          Cancel
        </HbsButton>
        <HbsButton id="delete-account-confirm-button" variant="danger" type="submit" disabled={!canSubmit} loading={busy}>
          {disbandOrgs ? `Delete account and ${familyNoun(disbandOrgs)}` : 'Delete account'}
        </HbsButton>
      </div>
    </form>
  );
}

interface SoleAdminBlockBodyProps {
  orgs: SoleAdminBlocker[];
  onCancel: () => void;
  onOpenFamilySettings: () => void;
  onDeleteWithFamilies: () => void;
}

function SoleAdminBlockBody({
  orgs,
  onCancel,
  onOpenFamilySettings,
  onDeleteWithFamilies,
}: SoleAdminBlockBodyProps): JSX.Element {
  const list = formatOrgList(orgs);
  const isPlural = orgs.length > 1;
  return (
    <>
      <p className="text-hbs-text">
        You're the only admin of {list}. Before you can delete your account,
        you'll need to either promote another member, remove all members, or
        delete {isPlural ? 'each family' : 'the family'}.
      </p>
      <p className="text-hbs-text-mute text-[13px] mt-3">
        Or delete {isPlural ? 'these families' : 'the family'} along with your
        account. Everyone in {isPlural ? 'them' : 'it'} will lose access to
        everything shared there.
      </p>
      <div className="mt-6 flex flex-wrap justify-end gap-2">
        <HbsButton id="delete-account-sole-admin-cancel-button" type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </HbsButton>
        <HbsButton id="delete-account-open-family-settings-button" variant="subtle" onClick={onOpenFamilySettings}>Open family settings</HbsButton>
        <HbsButton id="delete-account-with-families-button" variant="danger" onClick={onDeleteWithFamilies}>
          Delete account and {familyNoun(orgs)}
        </HbsButton>
      </div>
    </>
  );
}

interface SessionExpiredBodyProps {
  onCancel: () => void;
  onSignIn: () => void;
}

function SessionExpiredBody({ onCancel, onSignIn }: SessionExpiredBodyProps): JSX.Element {
  return (
    <>
      <p className="text-hbs-text">
        Sign in again, then try deleting your account from Settings.
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <HbsButton id="delete-account-session-expired-cancel-button" type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </HbsButton>
        <HbsButton id="delete-account-sign-in-again-button" onClick={onSignIn}>Sign in again</HbsButton>
      </div>
    </>
  );
}

interface NetworkErrorBodyProps {
  message: string;
  onCancel: () => void;
  onTryAgain: () => void;
}

function NetworkErrorBody({ message, onCancel, onTryAgain }: NetworkErrorBodyProps): JSX.Element {
  return (
    <>
      <p className="text-hbs-text">{message}</p>
      <div className="mt-6 flex justify-end gap-2">
        <HbsButton id="delete-account-network-error-cancel-button" type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </HbsButton>
        <HbsButton id="delete-account-try-again-button" onClick={onTryAgain}>Try again</HbsButton>
      </div>
    </>
  );
}
